import type { MouseEvent } from 'react'
import { useEffect } from 'react'
import { useAuth } from '@/components/auth/AuthProvider'
import { ClientPortalAccessSwitcher } from '@/components/client-portal/ClientPortalAccessSwitcher'
import { ClientPortalNotificationCenter } from '@/components/client-portal/ClientPortalNotificationCenter'
import { RecordButton } from '@/components/desktop/RecordButton'
import { ModeToggle } from '@/components/mode-toggle'
import { useTheme } from '@/components/theme-provider'
import { TimeEntryWidget } from '@/components/time-entry'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSidebar } from '@/components/ui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTauriTitleBar } from '@/contexts/TauriTitleBarContext'
import { useAccountType } from '@/hooks/useAccountType'
import { useAutoUpdater } from '@/hooks/useAutoUpdater'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { reloadPageWithRecordingGuard } from '@/hooks/useRecordingUnloadWarning'
import { useResolvedDarkMode } from '@/hooks/useResolvedDarkMode'
import { useTauriLiquidGlass } from '@/hooks/useTauriLiquidGlass'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { getShareableWebUrl } from '@/lib/utils/app-links'
import { useBennyStore } from '@/store/benny.store'
import { useClientPortalStore } from '@/store/client-portal.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '@/store/ui-preferences.store'
import { useUpdateStore } from '@/store/update.store'
import {
  getUserAppPreference,
  useUserPreferencesStore,
} from '@/store/user-preferences.store'
import {
  ArrowClockwiseIcon,
  ArrowLeftIcon,
  ArrowsClockwise,
  ArrowSquareOutIcon,
  CaretDownIcon,
  Download,
  ShareNetworkIcon,
  SidebarSimpleIcon,
} from '@phosphor-icons/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { AppPreferences } from '@scrambl/shared'

import { NotificationButton } from './NotificationButton'
import { OmniSearch } from './OmniSearch'
import { OrganizationSwitcher } from './OrganizationSwitcher'
import { UserMenu } from './UserMenu'

interface TauriTitleBarProps {
  onOpenChange?: (open: boolean) => void
}

const logger = createLogger('components/layout/TauriTitleBar')

function useTitleBarTextMode() {
  const sidebarTheme = useUIPreferencesStore(s => s.sidebarTheme)
  const { theme } = useTheme()
  const isDarkMode = useResolvedDarkMode(theme)
  // Personal spaces override the sidebar theme with hearth chrome (cream in
  // light, warm black in dark — see colors.css), so text mode must follow the
  // resolved light/dark mode, not the work sidebar theme. The DOM attribute is
  // stamped synchronously at boot (personal-theme.ts), so cold-boot popup
  // windows read correctly before the org store hydrates; useAccountType keeps
  // it reactive across org switches.
  const { isPersonal } = useAccountType()
  const isPersonalTheme =
    isPersonal ||
    (typeof document !== 'undefined' &&
      document.documentElement.getAttribute('data-account-type') === 'personal')

  const themeConfig =
    SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]
  const useDarkText = isPersonalTheme
    ? !isDarkMode
    : isDarkMode
      ? false
      : themeConfig.lightTextColor === 'dark'

  return { useDarkText }
}

function TauriUpdateButton() {
  const { updateAvailable, dismissed, installing } = useUpdateStore()
  const { installUpdate } = useAutoUpdater({ enableListeners: false })

  if (!updateAvailable || !dismissed) {
    return null
  }

  const buttonLabel = installing ? 'Updating...' : 'Install'

  const buttonIcon = installing ? (
    <ArrowsClockwise className="h-3 w-3 animate-spin !text-white" />
  ) : (
    <Download className="h-3 w-3 !text-white" weight="bold" />
  )

  return (
    <Button
      variant="default"
      size="sm"
      data-update-pill
      onClick={() => void installUpdate()}
      disabled={installing}
      className="update-pill-force h-7 gap-1 rounded-full !border-blue-500/70 !bg-blue-600 px-2.5 text-xs font-medium !text-white hover:!bg-blue-500 hover:!text-white [&_svg]:!text-white"
      title="Install available update"
    >
      {buttonIcon}
      <span className="!text-white">{buttonLabel}</span>
    </Button>
  )
}

/**
 * TauriTitleBar - A Stack-like title bar for the Tauri desktop app
 *
 * Features:
 * - Fixed position at the top of the window
 * - Draggable title bar using Tauri's startDragging() API
 * - Sidebar toggle on the left
 * - Centered OmniSearch that's also draggable when not focused
 * - Timer widget, organization switcher and user menu on the right
 * - Clean, minimal design inspired by Stack and Linear
 *
 * Traffic light position is set via trafficLightPosition in tauri.conf.json
 */
export function TauriTitleBar({ onOpenChange }: TauriTitleBarProps) {
  const { toggleSidebar, state } = useSidebar()
  const setSidebarOpen = useUIPreferencesStore(s => s.setSidebarOpen)
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  const navigate = useNavigate()
  const { setTitleBarActive } = useTauriTitleBar()
  const { useDarkText } = useTitleBarTextMode()
  useTauriLiquidGlass()
  const toggleBenny = useBennyStore(s => s.toggle)
  // When the floating pet is alive it IS the Benny entry point — hide the
  // header button to avoid two competing chat surfaces. Reactive: closing
  // the pet brings the button back.
  const companionActive = useBennyStore(s => s.companionActive)
  // With Simple Mode on, Benny lives in the sidebar ("Ask Benny") — hide the
  // title-bar face rather than offer two competing entry points.
  const { checkFeatureFlag } = useFeatureFlags(['SIMPLE_MODE'])
  const bennyInSidebar = checkFeatureFlag('SIMPLE_MODE')
  const storedPreferences = useUserPreferencesStore(s => s.preferences)
  const prefsInitialized = useUserPreferencesStore(s => s.isInitialized)
  const bennyAppPref = getUserAppPreference(storedPreferences)
  const bennyEnabled =
    prefsInitialized &&
    (bennyAppPref?.preferences as AppPreferences | undefined)?.bennyEnabled ===
      true &&
    !companionActive &&
    !bennyInSidebar

  // Mark title bar as active when mounted, inactive when unmounted
  useEffect(() => {
    setTitleBarActive(true)
    return () => setTitleBarActive(false)
  }, [setTitleBarActive])

  const handleToggleSidebar = () => {
    toggleSidebar()
    setSidebarOpen(state === 'collapsed')
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleRefresh = () => {
    reloadPageWithRecordingGuard()
  }

  const handlePopOut = async () => {
    const { openFocusWindow } = await import('@/lib/utils/tauri')
    const path = window.location.pathname
    const title = document.title.replace(' - Scrambl', '').trim() || 'Scrambl'
    await openFocusWindow(path, title)
  }

  const handleShare = async () => {
    const url = getShareableWebUrl()
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied to clipboard')
    } catch {
      toast.error('Failed to copy link')
    }
  }

  return (
    <div
      className={cn(
        'themed-titlebar bg-sidebar fixed left-0 right-0 top-0 z-50 transition-colors duration-300',
        tauriGlassEnabled && 'tauri-glass-titlebar',
      )}
      data-tauri-drag-region
    >
      {/* Title bar with drag region and controls - 52px height for centered traffic lights */}
      {/* CSS in index.css handles webkit-app-region for drag/no-drag based on data-tauri-drag-region */}
      <div
        className={cn('flex h-[52px] w-full items-center')}
        data-tauri-drag-region
      >
        {/* Left section - traffic light spacer + navigation + sidebar toggle + timer */}
        <div
          className="flex h-full flex-shrink-0 items-center gap-1 pl-[100px] pr-3"
          data-tauri-drag-region
        >
          {/* Back navigation button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className={cn(
              'h-8 w-8',
              useDarkText
                ? 'text-black/50 hover:bg-black/10 hover:text-black'
                : 'text-white/50 hover:bg-white/10 hover:text-white',
            )}
            title="Go back"
          >
            <ArrowLeftIcon className="h-[16px] w-[16px]" weight="bold" />
            <span className="sr-only">Go back</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            className={cn(
              'h-8 w-8',
              useDarkText
                ? 'text-black/50 hover:bg-black/10 hover:text-black'
                : 'text-white/50 hover:bg-white/10 hover:text-white',
            )}
            title="Refresh"
          >
            <ArrowClockwiseIcon className="h-[16px] w-[16px]" weight="bold" />
            <span className="sr-only">Refresh</span>
          </Button>
          {/* Secondary window actions collapse behind a chevron to keep the
              titlebar tight */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'h-8 w-6',
                  useDarkText
                    ? 'text-black/50 hover:bg-black/10 hover:text-black data-[state=open]:bg-black/10 data-[state=open]:text-black'
                    : 'text-white/50 hover:bg-white/10 hover:text-white data-[state=open]:bg-white/10 data-[state=open]:text-white',
                )}
                title="More window actions"
              >
                <CaretDownIcon className="h-[13px] w-[13px]" weight="bold" />
                <span className="sr-only">More window actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[10rem]">
              <DropdownMenuItem className="text-xs" onClick={handlePopOut}>
                <ArrowSquareOutIcon className="mr-2 h-3.5 w-3.5" />
                Open in new window
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-xs"
                onClick={() => void handleShare()}
              >
                <ShareNetworkIcon className="mr-2 h-3.5 w-3.5" />
                Copy web link
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div
            className={cn(
              'mx-1 h-5 w-px',
              useDarkText ? 'bg-black/10' : 'bg-white/10',
            )}
          />

          {/* Sidebar toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleSidebar}
            className={cn(
              'h-8 w-8',
              useDarkText
                ? 'text-black/70 hover:bg-black/10 hover:text-black'
                : 'text-white/70 hover:bg-white/10 hover:text-white',
            )}
          >
            <SidebarSimpleIcon className="h-[18px] w-[18px]" weight="bold" />
            <span className="sr-only">Toggle Sidebar</span>
          </Button>

          <div
            className={cn(
              'mx-1 h-5 w-px',
              useDarkText ? 'bg-black/10' : 'bg-white/10',
            )}
          />

          {/* Timer Widget - dynamic text colors based on theme */}
          <div
            className={cn(
              useDarkText
                ? '[&_.font-mono]:text-black [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70 [&_span]:text-black/90 [&_svg]:text-black/70'
                : '[&_.font-mono]:text-white [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70 [&_span]:text-white/90 [&_svg]:text-white/70',
            )}
          >
            <TimeEntryWidget />
          </div>

          {/* Meeting record widget - same dynamic text treatment */}
          <div
            className={cn(
              useDarkText
                ? '[&_.font-mono]:text-black [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70'
                : '[&_.font-mono]:text-white [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70',
            )}
          >
            <RecordButton />
          </div>
        </div>

        {/* Center section - OmniSearch + Benny */}
        <div
          className="flex min-w-0 flex-1 items-center justify-center px-4"
          data-tauri-drag-region
        >
          <div className="flex w-full max-w-xl items-center gap-2">
            <OmniSearch className="w-full" />
            {bennyEnabled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={toggleBenny}
                    data-benny-button
                    className={cn(
                      'h-8 w-8 flex-shrink-0 overflow-hidden rounded-full',
                      'bg-purple-hen',
                      'transition-tonal duration-200 hover:scale-105 active:scale-95',
                      'ring-purple-hen/20 ring-1',
                    )}
                    style={{
                      backgroundImage: 'url(/characters/Default.svg)',
                      backgroundSize: '140%',
                      backgroundPosition: 'center 10%',
                      backgroundRepeat: 'no-repeat',
                    }}
                    aria-label="Ask Benny"
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={4}>
                  Ask Benny
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Right section - utilities, org switcher, and user menu */}
        <div className="flex h-full flex-shrink-0 items-center gap-2 pr-4">
          {/* Wrap utility buttons with explicit text color for visibility */}
          <div
            className={cn(
              'flex items-center gap-1',
              useDarkText
                ? 'text-black/70 [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70 [&_svg]:text-black/70'
                : 'text-white/70 [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70 [&_svg]:text-white/70',
            )}
          >
            <ModeToggle />
            <NotificationButton />
          </div>
          <TauriUpdateButton />

          <OrganizationSwitcher className="max-w-[180px]" />
          <UserMenu
            onOpenChange={onOpenChange}
            getNavigationPath={path => path}
          />
        </div>
      </div>
    </div>
  )
}

export function TauriTitleBarBasic() {
  const navigate = useNavigate()
  const location = useLocation()
  const { setTitleBarActive } = useTauriTitleBar()
  const { useDarkText } = useTitleBarTextMode()
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  const { isAuthenticated } = useAuth()
  useTauriLiquidGlass()
  const isClientPortalRoute = location.pathname.startsWith('/client-portal')
  // Portal sidebar expand/collapse lives in the titlebar on desktop, matching
  // the main app's toggle placement.
  const portalSidebarCollapsed = useClientPortalStore(
    s => s.desktopSidebarCollapsed,
  )
  const setPortalSidebarCollapsed =
    useClientPortalStore.getState().setDesktopSidebarCollapsed
  const portalEntitySegment = isClientPortalRoute
    ? location.pathname.split('/').filter(Boolean)[1]
    : undefined
  const portalEntityId =
    portalEntitySegment && portalEntitySegment !== 'invite'
      ? portalEntitySegment
      : undefined

  useEffect(() => {
    setTitleBarActive(true)
    return () => setTitleBarActive(false)
  }, [setTitleBarActive])

  const handleBack = () => {
    navigate(-1)
  }

  const handleRefresh = () => {
    reloadPageWithRecordingGuard()
  }

  const handleMouseDown = async (event: MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return

    const target = event.target as HTMLElement
    if (
      target.closest(
        'button, a, input, select, textarea, [role="button"], [role="combobox"], [role="menu"], [role="menuitem"], [data-no-drag]',
      )
    ) {
      return
    }

    try {
      const { getCurrentWindow } = await import('@tauri-apps/api/window')
      await getCurrentWindow().startDragging()
    } catch (error) {
      logger.error('Failed to start window dragging:', error)
    }
  }

  return (
    <div
      className={cn(
        'themed-titlebar bg-sidebar fixed left-0 right-0 top-0 z-50 transition-colors duration-300',
        tauriGlassEnabled && 'tauri-glass-titlebar',
      )}
      data-tauri-drag-region
      onMouseDown={handleMouseDown}
    >
      <div
        className={cn('flex h-[52px] w-full items-center')}
        data-tauri-drag-region
      >
        <div
          className="flex h-full flex-shrink-0 items-center gap-1 pl-[100px] pr-3"
          data-tauri-drag-region
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className={cn(
              'h-8 w-8',
              useDarkText
                ? 'text-black/50 hover:bg-black/10 hover:text-black'
                : 'text-white/50 hover:bg-white/10 hover:text-white',
            )}
            title="Go back"
          >
            <ArrowLeftIcon className="h-[16px] w-[16px]" weight="bold" />
            <span className="sr-only">Go back</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            className={cn(
              'h-8 w-8',
              useDarkText
                ? 'text-black/50 hover:bg-black/10 hover:text-black'
                : 'text-white/50 hover:bg-white/10 hover:text-white',
            )}
            title="Refresh"
          >
            <ArrowClockwiseIcon className="h-[16px] w-[16px]" weight="bold" />
            <span className="sr-only">Refresh</span>
          </Button>
          {isClientPortalRoute && (
            <>
              <div
                className={cn(
                  'mx-1 h-5 w-px',
                  useDarkText ? 'bg-black/10' : 'bg-white/10',
                )}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  setPortalSidebarCollapsed(!portalSidebarCollapsed)
                }
                className={cn(
                  'h-8 w-8',
                  useDarkText
                    ? 'text-black/70 hover:bg-black/10 hover:text-black'
                    : 'text-white/70 hover:bg-white/10 hover:text-white',
                )}
                title="Toggle sidebar"
              >
                <SidebarSimpleIcon
                  className="h-[18px] w-[18px]"
                  weight="bold"
                />
                <span className="sr-only">Toggle sidebar</span>
              </Button>
            </>
          )}
        </div>

        <div
          className="flex min-w-0 flex-1 items-center justify-center px-4"
          data-tauri-drag-region
        />

        {/* Right section - mode toggle and user menu (only when authenticated) */}
        <div className="flex h-full flex-shrink-0 items-center gap-2 pr-4">
          <div
            className={cn(
              'flex items-center gap-1',
              useDarkText
                ? 'text-black/70 [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70 [&_svg]:text-black/70'
                : 'text-white/70 [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70 [&_svg]:text-white/70',
            )}
          >
            <ModeToggle
              showSidebarTheme={!isClientPortalRoute}
              showGlassToggle={!isClientPortalRoute}
            />
            {isClientPortalRoute && isAuthenticated ? (
              <ClientPortalNotificationCenter entityId={portalEntityId} />
            ) : null}
          </div>
          <TauriUpdateButton />
          {isClientPortalRoute && isAuthenticated ? (
            <div
              className={cn(
                'max-w-[220px]',
                useDarkText
                  ? '[&_.text-muted-foreground]:text-black/55 [&_button:hover]:bg-black/10 [&_button]:text-black/80'
                  : '[&_.text-muted-foreground]:text-white/60 [&_button:hover]:bg-white/10 [&_button]:text-white/90',
              )}
            >
              <ClientPortalAccessSwitcher />
            </div>
          ) : null}
          {isAuthenticated && (
            <UserMenu
              hideOrgSettings
              showHelpItems={false}
              getNavigationPath={path => path}
            />
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Height of the TauriTitleBar in pixels - used for offsetting content
 * 52px = h-[52px] for proper traffic light centering
 */
export const TAURI_TITLE_BAR_HEIGHT = 52

/**
 * Minimal navigation bar for popup/focus windows
 * Back and reload buttons only - thin and unobtrusive
 * No pop-out button since we're already in a popup window
 */
export function PopupNavBar() {
  const navigate = useNavigate()
  const location = useLocation()
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  const { useDarkText } = useTitleBarTextMode()
  useTauriLiquidGlass()
  const isSettingsWindow = location.pathname === '/desktop-settings'

  const handleBack = () => navigate(-1)
  const handleRefresh = () => reloadPageWithRecordingGuard()

  return (
    <div
      className={cn(
        'themed-titlebar bg-sidebar fixed left-0 right-0 top-0 z-50 transition-colors duration-300',
        tauriGlassEnabled && 'tauri-glass-titlebar',
      )}
      data-tauri-drag-region
    >
      <div
        className="relative flex h-8 items-center pl-[76px]"
        data-tauri-drag-region
      >
        {isSettingsWindow && (
          <span
            className={cn(
              'pointer-events-none absolute inset-0 flex items-center justify-center text-xs font-semibold',
              useDarkText ? 'text-black/65' : 'text-white/70',
            )}
            data-tauri-drag-region
          >
            Settings
          </span>
        )}
        {!isSettingsWindow && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className={cn(
              'h-6 w-6',
              useDarkText
                ? 'text-black/60 hover:bg-black/10 hover:text-black'
                : 'text-white/60 hover:bg-white/10 hover:text-white',
            )}
            title="Go back"
          >
            <ArrowLeftIcon className="h-3.5 w-3.5" weight="bold" />
          </Button>
        )}
        {!isSettingsWindow && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            className={cn(
              'h-6 w-6',
              useDarkText
                ? 'text-black/60 hover:bg-black/10 hover:text-black'
                : 'text-white/60 hover:bg-white/10 hover:text-white',
            )}
            title="Reload"
          >
            <ArrowClockwiseIcon className="h-3.5 w-3.5" weight="bold" />
          </Button>
        )}
      </div>
    </div>
  )
}

export const POPUP_NAV_BAR_HEIGHT = 32
